// /app/frontend/src/pages/ZohoCallback.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ZohoCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Connecting to Zoho...');
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false); // Prevent double-processing

  useEffect(() => {
    // Prevent React StrictMode double-call
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const state = searchParams.get('state');

      // Check for error from Zoho
      if (errorParam) {
        setStatus('error');
        setError(searchParams.get('error_description') || 'Authorization was denied');
        return;
      }

      // Check for authorization code
      if (!code) {
        setStatus('error');
        setError('No authorization code received');
        return;
      }

      setMessage('Exchanging authorization code...');

      try {
        const response = await fetch(`${BACKEND_URL}/api/zoho/oauth/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ code, state }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Failed to complete authorization');
        }

        setStatus('success');
        setMessage('Zoho Mail connected successfully!');

        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/admin');
        }, 3000);

      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setError(err.message || 'Failed to complete Zoho authorization');
      }
    };

    handleCallback();
  }, []); // Empty dependency array - run once only

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Alaska AI Services</h1>
          <p className="text-slate-400 mt-1">Zoho Mail Integration</p>
        </div>

        {/* Status Icon */}
        <div className="mb-6">
          {status === 'processing' && (
            <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500/20 rounded-full">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          )}
          {status === 'error' && (
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          )}
        </div>

        {/* Message */}
        <div className="mb-6">
          {status === 'processing' && (
            <p className="text-slate-300">{message}</p>
          )}
          {status === 'success' && (
            <>
              <p className="text-green-400 font-semibold text-lg">{message}</p>
              <p className="text-slate-400 mt-2">Redirecting to admin panel...</p>
            </>
          )}
          {status === 'error' && (
            <>
              <p className="text-red-400 font-semibold text-lg">Authorization Failed</p>
              <p className="text-slate-400 mt-2">{error}</p>
            </>
          )}
        </div>

        {/* Actions */}
        {status === 'error' && (
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = `${BACKEND_URL}/api/zoho/oauth/url`}
              className="w-full py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-colors"
            >
              Return to Admin Panel
            </button>
          </div>
        )}

        {status === 'success' && (
          <button
            onClick={() => navigate('/admin')}
            className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-colors"
          >
            Go to Admin Panel Now
          </button>
        )}
      </div>
    </div>
  );
};

export default ZohoCallback;
